import { Card, CardContent, CardHeader } from "@/components/ui/card.jsx";
import { Badge } from "@/components/ui/badge.jsx";
import { getStatusBadgeClass } from "@/lib/status-utils.js";
import { Accordion } from "@/components/ui/accordion.jsx";
import { LineChart, Wrench, Settings, Info, Layers, Flag } from "lucide-react";
import {
  OPERATING_CONDITIONS_LABELS,
  EQUIPMENT_INFO_LABELS,
  FLAG_DIAGNOSTIC_SECTIONS,
} from "./optimization-vitals-constants.js";
import {
  formatVitalDisplayValue,
  getOrderedVitalSigns,
  getVitalSignIcon,
} from "./optimization-vitals-utils.js";
import {
  getDefaultOpenFlagSectionValues,
  getFlagDiagnosticsTotalCount,
} from "./optimization-flag-utils.js";
import FlagCategorySection from "./flag-category-section.jsx";
import LabelValueCard from "./label-value-card.jsx";

/**
 * Vitals tab content: KPI cards, Warning Status & Actions, Flag Diagnostics, Operating Conditions, Equipment Info.
 */
export default function OptimizationVitals({
  normalized,
  operatingConditionsLoading = false,
  equipmentInfoLoading = false,
}) {
  const {
    vitalSigns = [],
    recommendedActions = [],
    severityLevel,
    flagDiagnostics = {},
    operatingConditions = {},
    equipmentInfo = {},
  } = normalized;

  const flagCount = getFlagDiagnosticsTotalCount(flagDiagnostics);
  const defaultOpenFlagSections =
    getDefaultOpenFlagSectionValues(flagDiagnostics);
  const orderedVitalSigns = getOrderedVitalSigns(vitalSigns);
  const isSeverityWarning = severityLevel && /warning/i.test(severityLevel);
  const severityBadge = severityLevel && (
    <Badge
      variant='status'
      className={getStatusBadgeClass(
        isSeverityWarning ? "alerted" : "offline",
        "inline-flex items-center gap-1.5"
      )}>
      <Info
        className={`h-4 w-4 shrink-0 ${isSeverityWarning ? "text-amber-600 dark:text-amber-400" : "text-red-600 dark:text-red-400"}`}
        strokeWidth={2.5}
      />
      <span>{severityLevel}</span>
    </Badge>
  );

  return (
    <div className='space-y-6'>
      <div className='grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3'>
        {orderedVitalSigns.map((v) => {
          const Icon = getVitalSignIcon(v.apiKey);
          const unit = v.unit ?? "";
          return (
            <Card
              key={v.apiKey}
              className='col-span-1 border-border bg-card shadow-sm transition-shadow hover:shadow'>
              <CardContent className='relative p-3'>
                <div className='flex justify-between items-start gap-2'>
                  <span className='text-xs font-medium uppercase tracking-wide text-muted-foreground leading-tight'>
                    {v.name}
                  </span>
                  {Icon && (
                    <Icon className='h-4 w-4 shrink-0 text-primary opacity-90' />
                  )}
                </div>
                <div className='mt-1.5 flex items-baseline justify-between gap-2'>
                  <p className='min-w-0 text-base font-bold text-card-foreground tabular-nums'>
                    {formatVitalDisplayValue(v)}
                    {unit && (
                      <span className='ml-0.5 font-normal text-muted-foreground'>
                        {unit}
                      </span>
                    )}
                  </p>
                  <LineChart className='h-3.5 w-3.5 shrink-0 text-muted-foreground/70' />
                </div>
                {v.subtitle && (
                  <p className='mt-1 text-xs text-muted-foreground'>
                    {v.subtitle}
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
        {recommendedActions.length > 0 && (
          <Card className='border-border bg-card shadow-sm'>
            <CardHeader className='pb-3'>
              <div className='flex items-center justify-between gap-4'>
                <span className='font-bold text-foreground'>
                  Warning Status & Actions
                </span>
                {severityBadge}
              </div>
            </CardHeader>
            <CardContent className='pt-0'>
              <p className='mb-3 text-xs font-bold uppercase tracking-wide text-muted-foreground'>
                Recommended Actions
              </p>
              <ul className='space-y-2'>
                {recommendedActions.map((action, i) => (
                  <li
                    key={i}
                    className='flex gap-2 rounded-lg bg-muted px-3 py-2.5'>
                    <Wrench className='h-4 w-4 shrink-0 text-muted-foreground' />
                    <span className='text-sm text-foreground'>{action}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        <Card className='border-border bg-card shadow-sm'>
          <CardHeader className='pb-3'>
            <div className='flex flex-wrap items-center justify-between gap-2'>
              <div className='flex items-center gap-2'>
                <Flag className='h-4 w-4 shrink-0 text-muted-foreground' />
                <span className='font-bold text-foreground'>
                  Flag Diagnostics
                </span>
              </div>
              <span
                className={
                  flagCount > 0
                    ? "rounded bg-muted text-foreground text-xs font-medium px-2.5 py-1"
                    : "rounded bg-muted text-muted-foreground text-xs font-medium px-2.5 py-1"
                }>
                {flagCount}
              </span>
            </div>
          </CardHeader>
          <CardContent className='pt-0'>
            <Accordion
              type='multiple'
              defaultValue={defaultOpenFlagSections}
              className='w-full'>
              {FLAG_DIAGNOSTIC_SECTIONS.map(({ value, key, Icon, label }) => (
                <FlagCategorySection
                  key={value}
                  accordionValue={value}
                  label={label}
                  Icon={Icon}
                  diagnostics={flagDiagnostics[key]}
                />
              ))}
            </Accordion>
          </CardContent>
        </Card>
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
        <LabelValueCard
          title='Operating Conditions'
          icon={Settings}
          labels={OPERATING_CONDITIONS_LABELS}
          data={operatingConditions}
          loading={operatingConditionsLoading}
        />
        <LabelValueCard
          title='Equipment Info'
          icon={Layers}
          labels={EQUIPMENT_INFO_LABELS}
          data={equipmentInfo}
          loading={equipmentInfoLoading}
        />
      </div>
    </div>
  );
}
